/**
 * Physical Therapy Discharge (Non-Visit) — options + empty payload.
 * After shared common header: Discharge Info → Disposition → Functional Status →
 * Reason → POC/Goals → Communication → Meds → Handoff → OASIS → Final Summary
 * (per screenshots). No wound section. Pattern matches PT Visit Note.
 */

import { emptyPtNotesCommonHeader } from "./ptNotesCommonHeaderUtils";

export const today = () => new Date().toISOString().slice(0, 10);

export const toggleInList = (list, value) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
};

export const YES_NO_OPTIONS = ["Yes", "No"];

export const YES_NO_NOT_REQUIRED_OPTIONS = ["Yes", "No", "Not required"];

export const PATIENT_CONTACTED_OPTIONS = [
  "Yes",
  "No",
  "Unable to contact",
  "Not applicable",
];

export const NON_VISIT_REASON_OPTIONS = [
  "Patient/caregiver refused discharge visit",
  "Patient unavailable for visit",
  "Patient moved out of service area",
  "Transferred to another provider",
  "Patient hospitalized",
  "Patient placed on hospice",
  "Physician/allowed practitioner directed discharge",
  "Patient no longer homebound",
  "Goals met / no further skilled PT need",
  "Insurance / coverage change",
  "Other",
];

export const DISCHARGE_DISPOSITION_OPTIONS = [
  "Remained in community without skilled services from another HHA or hospice",
  "Remained in community receiving skilled services from another Medicare-certified HHA",
  "Transferred to non-institutional hospice",
  "Moved to an area not served by this agency",
  "Hospital / acute care",
  "Unknown",
  "Other",
];

export const FUNCTIONAL_DOMAIN_OPTIONS = [
  "Bed Mobility",
  "Transfers",
  "Ambulation / Locomotion",
  "Stairs",
  "Balance",
  "Strength",
  "Endurance / Activity Tol.",
  "Pain Status",
  "Safety Awareness",
];

export const FUNCTIONAL_TREND_OPTIONS = [
  "Improved",
  "No Change",
  "Declined",
  "Unknown",
];

export const ASSISTIVE_DEVICE_OPTIONS = [
  "None",
  "Cane",
  "Walker",
  "Wheelchair",
  "Other",
];

export const DISCHARGE_REASON_OPTIONS = [
  "Goals met",
  "Goals partially met",
  "No further skilled PT need",
  "Maximum potential achieved",
  "Patient / caregiver independent with program",
  "Physician / allowed practitioner order",
  "Patient requested discharge",
  "Patient refused further PT services",
  "Patient transferred to another HHA",
  "Patient transferred to hospice",
  "Patient hospitalized",
  "Patient moved",
  "Insurance / coverage change",
  "Non-compliance with plan of care",
  "Unable to contact patient",
  "Other",
];

export const PLAN_OF_CARE_GOAL_OPTIONS = [
  "Goals met",
  "Goals partially met",
  "Goals not met",
  "Unable to assess due to non-visit discharge",
];

export const PRIOR_INSTRUCTION_OPTIONS = [
  "Home exercise program",
  "Activity progression",
  "Fall prevention",
  "Assistive device use",
  "Safe mobility / transfers",
  "Other",
];

export const PHYSICIAN_NOTIFY_METHOD_OPTIONS = [
  "Telephone",
  "Fax",
  "Secure electronic communication",
  "Other",
];

export const CAREGIVER_COMM_METHOD_OPTIONS = [
  "Telephone",
  "In person",
  "Patient portal",
  "Other",
];

export const DISCHARGE_INSTRUCTION_TOPIC_OPTIONS = [
  "Continue home exercise program",
  "Continue walking program / activity as tolerated",
  "Safety / fall prevention",
  "Follow up with physician / provider",
  "When to call MD / seek medical attention",
  "Other",
];

export const UNDERSTANDING_OPTIONS = [
  "Verbalized understanding",
  "Unable to assess",
  "Refused",
  "Other",
];

export const MEDICATION_RECONCILE_OPTIONS = [
  "Current medication information reviewed",
  "Medication list provided to patient / caregiver",
  "Medication list provided to subsequent provider",
  "Unable to review due to non-visit discharge",
  "Not applicable",
];

export const OUTSTANDING_TREATMENT_OPTIONS = ["None", "See notes", "Unknown"];

export const TRANSITION_INFO_OPTIONS = [
  "Relevant clinical information",
  "Plan of care / goals",
  "Assistive device recommendations",
  "Medication information (related to PT)",
  "PT evaluation / progress summary",
  "Home exercise program",
  "Precautions / restrictions",
  "Other",
];

export const OASIS_APPLICABILITY_OPTIONS = [
  "OASIS discharge assessment required",
  "OASIS discharge assessment not required",
  "Transfer / death situation - applicable OASIS requirements reviewed",
  "Other",
];

export const OASIS_ASSESSMENT_STATUS_OPTIONS = [
  "Completed",
  "Pending",
  "Not applicable",
  "Unable to complete - reason documented",
];

export const OASIS_SUBMISSION_OPTIONS = [
  "Pending",
  "Submitted",
  "Accepted",
  "Rejected",
];

export const emptyFunctionalStatusRow = (domain) => ({
  domain,
  trend: "",
});

export const emptyFunctionalStatusRows = () =>
  FUNCTIONAL_DOMAIN_OPTIONS.map((domain) => emptyFunctionalStatusRow(domain));

/** Full editable PT Discharge (Non-Visit) payload (JSON on dedicated row). */
export const emptyPtDischargeNonVisitPayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  commonHeader: emptyPtNotesCommonHeader(),

  certPeriodFrom: "",
  certPeriodTo: "",

  // —— 7. Discharge Information ——
  dischargeDate: "",
  dischargeTime: "",
  disciplinePt: true,
  therapistName: "",
  credentialsLicense: "",
  nonVisitReasons: [],
  nonVisitReasonOther: "",
  nonVisitExplanation: "",

  // —— 8. Discharge Disposition ——
  dischargeDisposition: "",
  dischargeDispositionOther: "",
  subsequentProviderName: "",
  subsequentProviderPhone: "",
  subsequentProviderAddress: "",

  // —— 9. Current Functional Status ——
  functionalStatus: emptyFunctionalStatusRows(),
  assistiveDevices: [],
  assistiveDeviceOther: "",
  weightBearingStatus: "",
  precautionsRestrictions: "",

  // —— 10. Reason for Discharge ——
  dischargeReasons: [],
  dischargeReasonOther: "",
  dischargeJustification: "",

  // —— 11. Plan of Care / Goals ——
  planOfCareGoalStatus: "",
  unresolvedProblems: "",
  recommendedContinuedCare: "",
  priorInstructions: [],
  priorInstructionOther: "",

  // —— 12. Communication ——
  physicianNotified: "",
  physicianNotifyDate: "",
  physicianNotifyTime: "",
  physicianNotifyMethods: [],
  physicianNotifyMethodOther: "",
  physicianNotifyPerson: "",
  physicianNotifyResponse: "",

  patientCaregiverContacted: "",
  caregiverCommDate: "",
  caregiverCommTime: "",
  caregiverCommMethods: [],
  caregiverCommMethodOther: "",
  caregiverCommPerson: "",
  dischargeInstructionTopics: [],
  dischargeInstructionOther: "",
  caregiverUnderstanding: [],
  caregiverUnderstandingOther: "",

  // —— 13. Medication / Treatment ——
  medicationReconcile: [],
  medicationConcerns: "",
  outstandingTreatments: "",
  outstandingTreatmentDetails: "",

  // —— 14. Transition / Handoff ——
  transitionInfo: [],
  transitionInfoOther: "",
  transitionDate: "",
  transitionMethod: "",
  transitionRecipient: "",

  // —— 15. OASIS / CMS Assessment Review ——
  oasisApplicability: [],
  oasisApplicabilityOther: "",
  oasisAssessmentStatus: [],
  oasisTimePoint: "",
  oasisSubmission: [],
  oasisComments: "",

  // —— 16. Final PT Summary ——
  finalClinicalSummary: "",
});

export const PT_DNV_ARRAY_KEYS = [
  "nonVisitReasons",
  "assistiveDevices",
  "dischargeReasons",
  "priorInstructions",
  "physicianNotifyMethods",
  "caregiverCommMethods",
  "dischargeInstructionTopics",
  "caregiverUnderstanding",
  "medicationReconcile",
  "transitionInfo",
  "oasisApplicability",
  "oasisAssessmentStatus",
  "oasisSubmission",
];
